import { useState, useMemo, useCallback } from 'react'

/**
 * Sortable + paginated table hook.
 * @param {Array} data - Raw data array
 * @param {object} options - { defaultSort, defaultDir, pageSize, storageKey }
 */
export function useSort(data, options = {}) {
  const { defaultSort = null, defaultDir = 'desc', pageSize = 25, storageKey } = options

  const [sortKey, setSortKey] = useState(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`sort-${storageKey}`)
      if (saved) {
        try { return JSON.parse(saved).key } catch { /* ignore */ }
      }
    }
    return defaultSort
  })

  const [sortDir, setSortDir] = useState(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`sort-${storageKey}`)
      if (saved) {
        try { return JSON.parse(saved).dir } catch { /* ignore */ }
      }
    }
    return defaultDir
  })

  const [page, setPage] = useState(0)

  const toggleSort = useCallback((key) => {
    let newDir = 'asc'
    if (sortKey === key) {
      newDir = sortDir === 'asc' ? 'desc' : sortDir === 'desc' ? null : 'asc'
    }
    if (newDir === null) {
      setSortKey(null)
      setSortDir('desc')
    } else {
      setSortKey(key)
      setSortDir(newDir)
    }
    setPage(0)
    if (storageKey && newDir) {
      localStorage.setItem(`sort-${storageKey}`, JSON.stringify({ key, dir: newDir }))
    }
  }, [sortKey, sortDir, storageKey])

  const sorted = useMemo(() => {
    if (!sortKey || !data) return data || []
    return [...data].sort((a, b) => {
      let va = resolve(a, sortKey)
      let vb = resolve(b, sortKey)

      // Handle nulls
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1

      // Numeric
      if (typeof va === 'number' || (typeof va === 'string' && !isNaN(va) && va !== '')) {
        va = Number(va)
        vb = Number(vb)
        return sortDir === 'asc' ? va - vb : vb - va
      }

      // Date
      if (va instanceof Date || (typeof va === 'string' && !isNaN(Date.parse(va)) && va.length > 8)) {
        const da = new Date(va).getTime()
        const db = new Date(vb).getTime()
        if (!isNaN(da) && !isNaN(db)) return sortDir === 'asc' ? da - db : db - da
      }

      // String
      const sa = String(va).toLowerCase()
      const sb = String(vb).toLowerCase()
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa)
    })
  }, [data, sortKey, sortDir])

  const totalPages = Math.ceil(sorted.length / pageSize)
  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize)

  return {
    sorted,
    paged,
    page,
    setPage,
    totalPages,
    total: sorted.length,
    sortKey,
    sortDir,
    toggleSort,
    pageSize,
  }
}

function resolve(obj, path) {
  return path.split('.').reduce((o, k) => o?.[k], obj)
}
